import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Search as SearchIcon,
  SelectAll as SelectAllIcon,
  DeselectOutlined as DeselectIcon,
} from '@mui/icons-material';
import { Virtuoso, GroupedVirtuoso } from 'react-virtuoso';
import { getTranslationValue } from '../../constants/posMenu';

const PANE_SX = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  minHeight: 0,
};

const getProductLabel = (product) => {
  const name = getTranslationValue(product.name) || 'Unnamed Product';
  const title = getTranslationValue(product.title);
  return title ? `${name} (${title})` : name;
};

const matchesSearch = (product, search) => {
  if (!search) return true;
  const term = search.toLowerCase();
  const name = getTranslationValue(product.name)?.toLowerCase() || '';
  const title = getTranslationValue(product.title)?.toLowerCase() || '';
  return name.includes(term) || title.includes(term);
};

const ConnectedProductsPickerDialog = ({
  open,
  onClose,
  allProducts,
  connectedProductIds,
  categories,
  modifierGroups,
  onSave,
  isSaving,
}) => {
  const [localConnectedIds, setLocalConnectedIds] = useState(new Set());
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [leftCategoryFilter, setLeftCategoryFilter] = useState(null);
  const [leftMgFilter, setLeftMgFilter] = useState([]);
  const [leftChecked, setLeftChecked] = useState(new Set());
  const [rightChecked, setRightChecked] = useState(new Set());

  // Reset only when the dialog opens; parent re-renders (any menu update)
  // must not wipe the user's unsaved picks
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      setLocalConnectedIds(new Set(connectedProductIds));
      setLeftSearch('');
      setRightSearch('');
      setLeftCategoryFilter(null);
      setLeftMgFilter([]);
      setLeftChecked(new Set());
      setRightChecked(new Set());
    }
  }, [open, connectedProductIds]);

  const itemToCategoryMap = useMemo(() => {
    const map = {};
    Object.entries(categories || {}).forEach(([catId, cat]) => {
      (cat.itemIds || []).forEach((itemId) => {
        if (!map[itemId]) map[itemId] = catId;
      });
    });
    allProducts.forEach((p) => {
      const direct = p.category || p.categoryId;
      if (direct && categories?.[direct]) {
        map[p._id] = direct;
      }
    });
    return map;
  }, [allProducts, categories]);

  const resolveCategory = useCallback(
    (product) => itemToCategoryMap[product._id] || null,
    [itemToCategoryMap]
  );

  const categoryOptions = useMemo(() => {
    const catSet = new Set();
    allProducts.forEach((p) => {
      const catId = resolveCategory(p);
      if (catId && categories?.[catId]) catSet.add(catId);
    });
    return Array.from(catSet)
      .map((catId) => ({
        id: catId,
        name: getTranslationValue(categories[catId]?.name) || catId,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allProducts, categories, resolveCategory]);

  const mgOptions = useMemo(() => {
    const mgSet = new Set();
    allProducts.forEach((p) => {
      (p.modifierGroupIds || []).forEach((mgId) => {
        if (modifierGroups?.[mgId]) mgSet.add(mgId);
      });
    });
    return Array.from(mgSet)
      .map((mgId) => ({
        id: mgId,
        name: getTranslationValue(modifierGroups[mgId]?.name) || mgId,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allProducts, modifierGroups]);

  const getCategoryNameResolved = useCallback(
    (product) => {
      const catId = resolveCategory(product);
      if (catId && categories?.[catId]) {
        return getTranslationValue(categories[catId].name) || 'Uncategorized';
      }
      return 'Uncategorized';
    },
    [resolveCategory, categories]
  );

  const availableProducts = useMemo(() => {
    const selectedMgIds =
      leftMgFilter.length > 0
        ? new Set(leftMgFilter.map((mg) => mg.id))
        : null;
    return allProducts
      .filter((p) => !localConnectedIds.has(p._id))
      .filter((p) => matchesSearch(p, leftSearch))
      .filter((p) => {
        if (!leftCategoryFilter) return true;
        return resolveCategory(p) === leftCategoryFilter.id;
      })
      .filter((p) => {
        if (!selectedMgIds) return true;
        const itemMgs = p.modifierGroupIds || [];
        return itemMgs.some((mgId) => selectedMgIds.has(mgId));
      })
      .sort((a, b) => {
        const catA = getCategoryNameResolved(a);
        const catB = getCategoryNameResolved(b);
        if (catA !== catB) return catA.localeCompare(catB);
        const nameA = getTranslationValue(a.name)?.toLowerCase() || '';
        const nameB = getTranslationValue(b.name)?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
  }, [
    allProducts,
    localConnectedIds,
    leftSearch,
    leftCategoryFilter,
    leftMgFilter,
    resolveCategory,
    getCategoryNameResolved,
  ]);

  const connectedProducts = useMemo(() => {
    return allProducts
      .filter((p) => localConnectedIds.has(p._id))
      .filter((p) => matchesSearch(p, rightSearch))
      .sort((a, b) => {
        const nameA = getTranslationValue(a.name)?.toLowerCase() || '';
        const nameB = getTranslationValue(b.name)?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
  }, [allProducts, localConnectedIds, rightSearch]);

  const changeCount = useMemo(() => {
    let adds = 0;
    let removes = 0;
    localConnectedIds.forEach((id) => {
      if (!connectedProductIds.has(id)) adds++;
    });
    connectedProductIds.forEach((id) => {
      if (!localConnectedIds.has(id)) removes++;
    });
    return { adds, removes, total: adds + removes };
  }, [localConnectedIds, connectedProductIds]);

  const handleMoveRight = useCallback(() => {
    if (leftChecked.size === 0) return;
    setLocalConnectedIds((prev) => {
      const next = new Set(prev);
      leftChecked.forEach((id) => next.add(id));
      return next;
    });
    setLeftChecked(new Set());
  }, [leftChecked]);

  const handleMoveLeft = useCallback(() => {
    if (rightChecked.size === 0) return;
    setLocalConnectedIds((prev) => {
      const next = new Set(prev);
      rightChecked.forEach((id) => next.delete(id));
      return next;
    });
    setRightChecked(new Set());
  }, [rightChecked]);

  const toggleLeftCheck = useCallback((id) => {
    setLeftChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleRightCheck = useCallback((id) => {
    setRightChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllLeft = useCallback(() => {
    setLeftChecked(new Set(availableProducts.map((p) => p._id)));
  }, [availableProducts]);

  const deselectAllLeft = useCallback(() => {
    setLeftChecked(new Set());
  }, []);

  const selectAllRight = useCallback(() => {
    setRightChecked(new Set(connectedProducts.map((p) => p._id)));
  }, [connectedProducts]);

  const deselectAllRight = useCallback(() => {
    setRightChecked(new Set());
  }, []);

  const handleSave = () => {
    const addedIds = [];
    const removedIds = [];
    localConnectedIds.forEach((id) => {
      if (!connectedProductIds.has(id)) addedIds.push(id);
    });
    connectedProductIds.forEach((id) => {
      if (!localConnectedIds.has(id)) removedIds.push(id);
    });
    onSave(addedIds, removedIds);
  };

  const groupBoundaries = useMemo(() => {
    if (availableProducts.length === 0) return { groups: [], counts: [] };
    const groups = [];
    const counts = [];
    let currentCat = null;
    let currentCount = 0;
    availableProducts.forEach((p) => {
      const cat = getCategoryNameResolved(p);
      if (cat !== currentCat) {
        if (currentCat !== null) {
          counts.push(currentCount);
        }
        groups.push(cat);
        currentCat = cat;
        currentCount = 1;
      } else {
        currentCount++;
      }
    });
    if (currentCat !== null) counts.push(currentCount);
    return { groups, counts };
  }, [availableProducts, getCategoryNameResolved]);

  const LeftItemContent = useCallback(
    (index) => {
      const product = availableProducts[index];
      if (!product) return null;
      const checked = leftChecked.has(product._id);
      return (
        <ListItem disablePadding dense>
          <ListItemButton
            onClick={() => toggleLeftCheck(product._id)}
            dense
            sx={{ py: 0.25 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox
                edge="start"
                checked={checked}
                tabIndex={-1}
                disableRipple
                size="small"
              />
            </ListItemIcon>
            <ListItemText
              primary={getProductLabel(product)}
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
            />
          </ListItemButton>
        </ListItem>
      );
    },
    [availableProducts, leftChecked, toggleLeftCheck]
  );

  const RightItemContent = useCallback(
    (index) => {
      const product = connectedProducts[index];
      if (!product) return null;
      const checked = rightChecked.has(product._id);
      return (
        <ListItem disablePadding dense>
          <ListItemButton
            onClick={() => toggleRightCheck(product._id)}
            dense
            sx={{ py: 0.25 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox
                edge="start"
                checked={checked}
                tabIndex={-1}
                disableRipple
                size="small"
              />
            </ListItemIcon>
            <ListItemText
              primary={getProductLabel(product)}
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
            />
            <Chip
              label={getCategoryNameResolved(product)}
              size="small"
              variant="outlined"
              sx={{ ml: 1, flexShrink: 0, maxWidth: 140, fontSize: '0.7rem' }}
            />
          </ListItemButton>
        </ListItem>
      );
    },
    [connectedProducts, rightChecked, toggleRightCheck, getCategoryNameResolved]
  );

  const hasActiveFilters =
    leftSearch || leftCategoryFilter || leftMgFilter.length > 0;

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: 800,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
        }}
      >
        <Typography variant="h6" component="span">
          Manage Connected Products
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={isSaving}
          size="small"
          edge="end"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Filters row - above both panes */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          bgcolor: '#fafafa',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        <Autocomplete
          size="small"
          sx={{ minWidth: 180, flex: 1 }}
          options={categoryOptions}
          value={leftCategoryFilter}
          onChange={(_, val) => setLeftCategoryFilter(val)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter by Category"
              placeholder="All categories"
            />
          )}
        />
        <Autocomplete
          multiple
          size="small"
          sx={{ minWidth: 220, flex: 2 }}
          options={mgOptions}
          value={leftMgFilter}
          onChange={(_, val) => setLeftMgFilter(val)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Checkbox
                size="small"
                checked={selected}
                sx={{ mr: 1, p: 0 }}
              />
              {option.name}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
                sx={{ maxWidth: 150, height: 22, '& .MuiChip-label': { fontSize: '0.75rem' } }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter by Modifier Groups"
              placeholder={leftMgFilter.length === 0 ? 'All modifier groups' : ''}
            />
          )}
          limitTags={2}
        />
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={() => {
              setLeftCategoryFilter(null);
              setLeftMgFilter([]);
              setLeftSearch('');
            }}
            sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
          >
            Clear filters
          </Button>
        )}
      </Box>

      <DialogContent
        sx={{
          display: 'flex',
          gap: 1.5,
          p: 2,
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left Pane - Available Products */}
        <Box sx={PANE_SX}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: '#fafafa',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.75,
              }}
            >
              <Typography variant="subtitle2">
                Available ({availableProducts.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                  onClick={selectAllLeft}
                  disabled={availableProducts.length === 0}
                  startIcon={<SelectAllIcon sx={{ fontSize: '1rem !important' }} />}
                >
                  All
                </Button>
                <Button
                  size="small"
                  sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                  onClick={deselectAllLeft}
                  disabled={leftChecked.size === 0}
                  startIcon={<DeselectIcon sx={{ fontSize: '1rem !important' }} />}
                >
                  None
                </Button>
              </Box>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search available..."
              value={leftSearch}
              onChange={(e) => setLeftSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {availableProducts.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {hasActiveFilters
                    ? 'No matching products'
                    : 'All products are connected'}
                </Typography>
              </Box>
            ) : (
              <GroupedVirtuoso
                style={{ height: '100%' }}
                groupCounts={groupBoundaries.counts}
                groupContent={(index) => (
                  <Box
                    sx={{
                      bgcolor: '#eeeeee',
                      px: 1.5,
                      py: 0.4,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.68rem',
                      }}
                    >
                      {groupBoundaries.groups[index]}
                    </Typography>
                  </Box>
                )}
                itemContent={LeftItemContent}
              />
            )}
          </Box>
        </Box>

        {/* Center Controls */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 0.5,
            minWidth: 44,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleMoveRight}
            disabled={leftChecked.size === 0 || isSaving}
            sx={{ minWidth: 36, px: 0.5 }}
          >
            <ChevronRightIcon />
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleMoveLeft}
            disabled={rightChecked.size === 0 || isSaving}
            sx={{ minWidth: 36, px: 0.5 }}
          >
            <ChevronLeftIcon />
          </Button>
        </Box>

        {/* Right Pane - Connected Products */}
        <Box sx={PANE_SX}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: '#fafafa',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.75,
              }}
            >
              <Typography variant="subtitle2">
                Connected ({connectedProducts.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                  onClick={selectAllRight}
                  disabled={connectedProducts.length === 0}
                  startIcon={<SelectAllIcon sx={{ fontSize: '1rem !important' }} />}
                >
                  All
                </Button>
                <Button
                  size="small"
                  sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                  onClick={deselectAllRight}
                  disabled={rightChecked.size === 0}
                  startIcon={<DeselectIcon sx={{ fontSize: '1rem !important' }} />}
                >
                  None
                </Button>
              </Box>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search connected..."
              value={rightSearch}
              onChange={(e) => setRightSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {connectedProducts.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {rightSearch
                    ? 'No matching products'
                    : 'No products connected yet'}
                </Typography>
              </Box>
            ) : (
              <Virtuoso
                style={{ height: '100%' }}
                totalCount={connectedProducts.length}
                itemContent={RightItemContent}
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={changeCount.total === 0 || isSaving}
          startIcon={
            isSaving ? (
              <CircularProgress size={18} color="inherit" />
            ) : null
          }
        >
          {isSaving
            ? 'Saving...'
            : changeCount.total === 0
              ? 'No changes'
              : `Save (${changeCount.adds > 0 ? `+${changeCount.adds}` : ''}${changeCount.adds > 0 && changeCount.removes > 0 ? ', ' : ''}${changeCount.removes > 0 ? `-${changeCount.removes}` : ''})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectedProductsPickerDialog;
